import React, { useEffect, useState } from "react";
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { EndPoints } from "../../../../Constants/endpoints";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../../../Services/ToastContext";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import WanringIcon from "../../../../assets/images/alert-triangle.svg";
import { Methods } from "../../../../Constants/ApiConstants";
import { useTranslation } from "react-i18next";
import { usePostData } from "../../../../hooks";
import {
  GeneralButton,
  GeneralCheckbox,
  GeneralInput,
  GeneralRadio,
} from "../../../../components";
import Modal, { ModalProps } from "../../../../components/Common/Modal";

interface SaveRecordAndTemplateConfirmProps extends ModalProps {
  //TODO
  onSend: (TemplateId?: number) => any;
}

const SaveRecordAndTemplateConfirm: React.FC<
  SaveRecordAndTemplateConfirmProps
> = ({ isOpen, onClose, onSend }) => {
  const { t } = useTranslation();

  const [isSendReport, setIsSendReport] = useState<boolean>(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState<boolean>(false);
  const [isSaveAsNew, setIsSaveAsNew] = useState<boolean>(false);

  const medicalRecordTemplate: any =
    useSelector((state: RootState) => state.medicalRecord.template) ?? {};

  const methods = useForm();
  const { PatientId, templateId } = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { mutate, isPending } = usePostData(
    EndPoints.CreateMedicalHistoryTypes,
  );
  const { mutate: mutateUpdate } = usePostData(
    `${EndPoints.UpdateMedicalTemplate}/${templateId}`,
    Methods.PUT,
  );

  const { showToast } = useToast();
  const location = useLocation();

  const handleBack = () => {
    navigate(location.state?.from || -1);
  };

  const name = "saveNew";

  const onSubmit: SubmitHandler<FieldValues> = (newNames) => {
    if (!isSaveAsNew) {
      onSend(medicalRecordTemplate?.Id);
      mutateUpdate(medicalRecordTemplate, {
        onSuccess: (data) => {
          showToast(t("success"), "success");
          queryClient.refetchQueries({
            queryKey: [EndPoints.MedicalHistoryTypes],
            exact: false,
          });
          queryClient.refetchQueries({
            queryKey: [EndPoints.GetMostUsedMedicalHistoryType],
            exact: false,
          });
          PatientId
            ? navigate(`/patients_list/${PatientId}/medical-records`)
            : handleBack();
        },
        onError: (error: any) =>
          showToast(error.message || t("failed"), "error"),
      });
    } else {
      mutate(
        {
          ...medicalRecordTemplate,
          ...newNames,
          MedicalHistoryTypeId: templateId,
        },
        {
          onSuccess: (data) => {
            onSend(data?.Data?.TemplateId);
            onClose && onClose();

            showToast(t("success"), "success");
            queryClient.refetchQueries({
              queryKey: [EndPoints.MedicalHistoryTypes],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [EndPoints.GetAllPatientMedicalRecords],
              exact: false,
            });
            queryClient.refetchQueries({
              queryKey: [EndPoints.GetMostUsedMedicalHistoryType],
              exact: false,
            });
            PatientId
              ? navigate(`/patients_list/${PatientId}/medical-records`)
              : handleBack();
          },
          onError: (error: any) =>
            showToast(error.message || t("failed"), "error"),
        },
      );
    }
  };

  const stepContent = !isSavingTemplate ? (
    <>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <GeneralRadio
            checked={isSendReport}
            className={
              isSendReport ? "!text-base !text-primary-text" : "!text-base"
            }
            label={t("send_report")}
            onClick={() => setIsSendReport(true)}
          />
          <p className="text-body-text">{t("send_report_message")}</p>
        </div>
        <div className="flex flex-col gap-4">
          <GeneralRadio
            className={
              !isSendReport ? "!text-base !text-primary-text" : "!text-base"
            }
            checked={!isSendReport}
            label={t("save_as_template")}
            onClick={() => setIsSendReport(false)}
          />
          <p className="text-body-text">{t("save_as_template_message")}</p>
        </div>
      </div>
      <div className="ms-auto mt-8 flex w-fit gap-3">
        <GeneralButton
          className="!text-primary-text"
          color="lite"
          onClick={onClose}
          title={t("cancel")}
        />
        <GeneralButton
          onClick={() => {
            if (isSendReport) {
              onSend(templateId ?? medicalRecordTemplate?.Id);
            } else {
              setIsSavingTemplate(true);
            }
          }}
          title={t("submit")}
        />
      </div>
    </>
  ) : (
    <div className="flex flex-col">
      <div className="mb-6 flex gap-2 rounded-lg border border-orange bg-[#FFB00040] p-2">
        <img src={WanringIcon} alt="warning" />
        <p className="text-sm text-primary-text">
          {t("template_update_warning")}
        </p>
      </div>
      <GeneralCheckbox
        readOnly={!medicalRecordTemplate?.AccountId}
        defaultChecked={!medicalRecordTemplate?.AccountId}
        name={"saveNew"}
        onChange={(event) => {
          setIsSaveAsNew(event.target.checked);
        }}
        label={t("save_as_new_template")}
      />
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-3"
        >
          {isSaveAsNew && (
            <>
              <div className="mb-6 mt-3 flex flex-col gap-2">
                <h2 className="text-text-muted">{t("template_name_en")}</h2>

                <GeneralInput
                  required
                  customStyleClasses="!bg-white !border-body-text !border placeholder:!text-text-muted !h-[50px]"
                  placeholder={t("template_name_en")}
                  name={"NameEn"}
                />
              </div>
              <div className="flex flex-col gap-2">
                <h2 className="text-text-muted">{t("template_name_ar")}</h2>
                <GeneralInput
                  required
                  customStyleClasses="!bg-white !border-body-text !border placeholder:!text-text-muted !h-[50px]"
                  placeholder={t("template_name_ar")}
                  name={"NameAr"}
                />
              </div>
            </>
          )}
          <div className="ms-auto mt-4 flex w-fit gap-3">
            <GeneralButton
              color="lite"
              className="!text-primary-text"
              onClick={onClose}
              title={t("cancel")}
            />
            <GeneralButton
              isLoading={isPending}
              type="submit"
              title={t("save_template")}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );

  useEffect(() => {
    methods.setValue(name, !medicalRecordTemplate?.AccountId);
    setIsSaveAsNew(!medicalRecordTemplate?.AccountId);
  }, []);

  useEffect(() => {
    setIsSaveAsNew(methods.watch(name));
  }, [methods.watch(name)]);

  return (
    <Modal
      header={<p className="!text-base">{t("send_the_report")}</p>}
      customStyle="min-w-[46vw]"
      isOpen={isOpen}
      onClose={onClose}
    >
      {stepContent}
    </Modal>
  );
};

export default SaveRecordAndTemplateConfirm;
